// Alert service. Fetches and updates safety alerts for teacher/admin dashboards.
import { apiGet, apiPatch, apiPost } from './apiClient';

export interface AlertDetail {
  id: string;
  title: string;
  description: string;
  type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  status: string;
  sourceType: string;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
  resolutionNotes: string | null;
  student: {
    id: string;
    name: string;
    email: string;
    avatarUrl: string | null;
    recentActivity: Array<{
      id: string;
      eventType: string;
      content: string | null;
      url: string | null;
      domain: string | null;
      isFlagged: boolean;
      flagReason: string | null;
      createdAt: string;
    }>;
  };
  school: { name: string };
  assignedTo: { id: string; name: string; email: string } | null;
  resolvedBy: { id: string; name: string } | null;
  history: Array<{
    id: string;
    previousStatus: string | null;
    newStatus: string;
    notes: string | null;
    changedBy: string;
    createdAt: string;
  }>;
  mlResult: {
    detectionType: string;
    confidence: number;
    severity: string;
    modelVersion: string;
    inputText: string | null;
  } | null;
}

export interface AlertActionResponse {
  success: boolean;
  alert: {
    id: string;
    title: string;
    status: string;
    severity: string;
    studentName: string;
    assignedTo: string | null;
  };
  message: string;
}

export const alertService = {
  async getAlertDetail(id: string): Promise<AlertDetail> {
    const response = await apiGet<{ success: boolean; alert: AlertDetail }>(`/alerts/${id}`);
    return response.alert;
  },

  async updateAlertStatus(id: string, status: string, notes?: string): Promise<AlertActionResponse> {
    return apiPatch<AlertActionResponse>(`/alerts/${id}/status`, { status, notes });
  },

  async acknowledgeAlert(id: string, notes?: string): Promise<AlertActionResponse> {
    return this.updateAlertStatus(id, 'acknowledged', notes);
  },

  async investigateAlert(id: string, notes?: string): Promise<AlertActionResponse> {
    return this.updateAlertStatus(id, 'investigating', notes);
  },

  async resolveAlert(id: string, notes?: string): Promise<AlertActionResponse> {
    return this.updateAlertStatus(id, 'resolved', notes);
  },

  async escalateAlert(id: string, notes?: string): Promise<AlertActionResponse> {
    return this.updateAlertStatus(id, 'escalated', notes);
  },

  async dismissAlert(id: string, notes?: string): Promise<AlertActionResponse> {
    return this.updateAlertStatus(id, 'dismissed', notes);
  },

  async assignToSelf(id: string): Promise<{ success: boolean; assignedTo: { name: string; email: string } | null; message: string }> {
    return apiPatch(`/alerts/${id}/assign`);
  },

  async logParentContact(id: string, note: string): Promise<{ success: boolean; message: string }> {
    return apiPost(`/alerts/${id}/contact-log`, { note });
  },
};
